// The dashboard's single fan-out: widgets are pure consumers, since a page where each one queries on mount gets slow
import { useCallback, useMemo } from "react";
import { useIsFetching, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { listProcessedModelSchedules } from "../graphql/queries";
import {
  listAssetGroupsForOperations,
  listComposedForecastsCustom,
  listOutputParamsWithLatestForecast,
} from "../graphql/customQueries";
import { fetchPreviewData } from "../utils/composedForecastUtils";
import { listAllPages } from "../utils/graphqlPagination";
import { getAdminOwnerFilter } from "../utils/ownerScope";
import { weightPreviewData } from "../utils/composedForecastWeight";
import {
  ProcessedModelRunHistoryService,
  byCreatedAtDesc,
  type ProcessedModelRunHistory,
} from "../services/processedModelRunHistoryService";
import { ScheduleService, type AgentSchedule } from "../services/scheduleService";
import {
  assetCards,
  assetForecastPairs,
  compareGenerationToForecast,
  deriveSignals,
  groupSignals,
  latestAnalystInsights,
  mergeAgentSchedule,
  operationsKpis,
  composedForecastTileId,
  generationHeadline,
  generationKpi,
  selectGenerationPairs,
  selectNeedsAttention,
  summarizeAgents,
  runFetchHours,
  DEFAULT_TIME_WINDOW,
  type AssetGroupLite,
  type AssetCard,
  type CandidateSeries,
  type ComposedForecastLite,
  type GenerationPair,
  type GenerationSeriesSource,
  type Kpi,
  type ModelSchemaName,
  type GenerationComparison,
  type OperationsData,
  type OutputParamLite,
  type TimeWindow,
  type RunnerConversation,
  type ProcessedModelScheduleLite,
} from "../utils/operationsDashboard";


// Every dashboard query sits under this prefix, so one invalidation reloads the board, the chart and every tile
const OPERATIONS_QUERY_KEY = ["operationsDashboard"] as const;

// Agent runs are a per-schedule query, so the fan-out is capped rather than left to grow with the org
const AGENT_RUN_SCHEDULE_CAP = 12;
const AGENT_RUNS_PER_SCHEDULE = 5;
const MODEL_SCHEMA_PAGE_SIZE = 500;
// 500 a page, so 10,000 runs per model before the oldest are dropped
const RUN_PAGES_PER_MODEL = 20;
const RUN_QUERY_CONCURRENCY = 8;
const OUTPUT_PARAM_PAGE_SIZE = 300;
const COMPOSED_FORECAST_PAGE_SIZE = 100;
const ASSET_GROUP_PAGE_SIZE = 100;
// Assets nested under a group. AppSync caps a connection at 100 unless asked, and a silently short map is worse than a slow one.
const ASSETS_PER_GROUP = 500;

// Pages to the end; a failed page keeps earlier results and names the source in `failures` so a partial list is flagged
const fetchAllPages = async <T>(
  query: string,
  root: string,
  variables: Record<string, unknown>,
  label: string,
  failures?: string[]
): Promise<T[]> => {
  const { items, complete, error } = await listAllPages<T>(query, root, variables, { label, keepOnError: true });
  if (error) {
    console.error(`[useOperationsData] Error listing ${label}:`, error);
    failures?.push(label);
  } else if (!complete) {
    // Owner filters run after DynamoDB's read limit, so hitting the ceiling can hide rows without any error
    failures?.push(`${label} (only part of the list)`);
  }
  return items;
};

// For the services, which swallow by default: asked to throw, a failure is named in `failures` instead of passing as an empty list
const recordFailure = async <T>(
  label: string,
  failures: string[],
  load: () => Promise<T[]>
): Promise<T[]> => {
  try {
    return await load();
  } catch (error) {
    console.error(`[useOperationsData] Error listing ${label}:`, error);
    failures.push(label);
    return [];
  }
};

const fetchModelSchedules = (
  filter?: object,
  failures?: string[]
): Promise<ProcessedModelScheduleLite[]> =>
  fetchAllPages<ProcessedModelScheduleLite>(
    listProcessedModelSchedules,
    "listProcessedModelSchedules",
    { filter, limit: 200 },
    "model schedules",
    failures
  );

const fetchOutputParams = (filter?: object, failures?: string[]): Promise<OutputParamLite[]> =>
  fetchAllPages<OutputParamLite>(
    listOutputParamsWithLatestForecast,
    "listProcessedModelOutputParams",
    { filter, limit: OUTPUT_PARAM_PAGE_SIZE },
    "forecast feeds",
    failures
  );

// Bounded second wave: newest answer only, and only for the handful of runners most likely to have one
const RUNNER_CONVERSATION_CAP = 6;

const fetchRunnerConversations = async (
  schedules: AgentSchedule[]
): Promise<RunnerConversation[]> => {
  const runners = schedules
    .flatMap((schedule) =>
      (schedule.runners?.items || []).map((runner) => ({ scheduleId: schedule.id, runner }))
    )
    .slice(0, RUNNER_CONVERSATION_CAP);

  const batches = await Promise.all(
    runners.map(async ({ scheduleId, runner }) => {
      const conversations = await ScheduleService.getRunnerConversations(runner.id, 1);
      return conversations.map((conversation) => ({ scheduleId, conversation }));
    })
  );

  return batches.flat();
};

const fetchAssetGroups = (filter?: object, failures?: string[]): Promise<AssetGroupLite[]> =>
  fetchAllPages<AssetGroupLite>(
    listAssetGroupsForOperations,
    "listAssetGroups",
    { filter, limit: ASSET_GROUP_PAGE_SIZE, assetLimit: ASSETS_PER_GROUP },
    "asset groups",
    failures
  );

const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  load: (item: T) => Promise<R>
): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await load(items[index]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
};

// Run history is its own query keyed by range, so changing the range doesn't refetch the rest
type BoardData = Omit<OperationsData, "runs" | "modelSchemas">;

interface RunHistoryData {
  runs: ProcessedModelRunHistory[];
  modelSchemas: ModelSchemaName[];
  failedSources: string[];
  // The range these runs cover, so a narrower range still on screen can be told from the one asked for
  hours: number;
}

// One index query per model, bounded by the range rather than the table's size
const fetchRunHistory = async (hours: number): Promise<RunHistoryData> => {
  const failedSources: string[] = [];
  const ownerFilter = await getAdminOwnerFilter();
  const schemas = await recordFailure("run history", failedSources, () =>
    ProcessedModelRunHistoryService.listSchemas(MODEL_SCHEMA_PAGE_SIZE, {
      filter: ownerFilter,
      throwOnError: true,
      onTruncated: () => failedSources.push("models (only part of the list)"),
    })
  );

  const since = new Date(Date.now() - hours * 3_600_000).toISOString();
  let truncated = false;
  let failedModels = 0;
  const batches = await mapWithConcurrency(schemas, RUN_QUERY_CONCURRENCY, async (schema) => {
    try {
      return await ProcessedModelRunHistoryService.listRunsBySchemaSince(schema.id, since, {
        maxPages: RUN_PAGES_PER_MODEL,
        onTruncated: () => {
          truncated = true;
        },
      });
    } catch (error) {
      console.error(`[useOperationsData] Error listing runs for model ${schema.id}:`, error);
      failedModels += 1;
      return [];
    }
  });

  if (failedModels > 0) {
    failedSources.push(`run history for ${failedModels} of ${schemas.length} models`);
  }
  if (truncated) failedSources.push("run history (only part of the list)");

  return {
    runs: batches.flat().sort(byCreatedAtDesc),
    modelSchemas: schemas.map(({ id, name }) => ({ id, name })),
    failedSources,
    hours,
  };
};

const fetchOperationsData = async (): Promise<BoardData> => {
  // Owner-scoped because four of these models give Admin a global read; AgentSchedule(Run) is already narrowed
  const ownerFilter = await getAdminOwnerFilter();
  // Filled by every source that fails, so the board can say what it could not load rather than read as an all-clear
  const failedSources: string[] = [];

  const [agentSchedules, modelSchedules, outputParams, assetGroups] = await Promise.all([
    recordFailure("agents", failedSources, () =>
      ScheduleService.listSchedules({ throwOnError: true })
    ),
    fetchModelSchedules(ownerFilter, failedSources),
    fetchOutputParams(ownerFilter, failedSources),
    fetchAssetGroups(ownerFilter, failedSources),
  ]);

  // Ordered by last activity, not filtered to ACTIVE — gating on status hid 232 runs of Snowpack Analyst
  const recentlyRun = agentSchedules
    .filter((schedule) => schedule.status !== "DELETED")
    .sort(
      (a, b) =>
        new Date(b.lastRunAt || 0).getTime() - new Date(a.lastRunAt || 0).getTime()
    )
    .slice(0, AGENT_RUN_SCHEDULE_CAP);

  const runBatches = await Promise.all(
    recentlyRun.map((schedule) =>
      ScheduleService.getScheduleRuns(schedule.id, AGENT_RUNS_PER_SCHEDULE)
    )
  );

  const agentRuns = runBatches.flat();

  // Two links run back to an agent; fall back to the runner link per schedule, so one direct-linked agent cannot hide the rest
  const directlyLinked = new Set(
    agentRuns
      .filter((run) => (run.conversations?.items || []).length > 0)
      .map((run) => run.scheduleId)
  );
  const runnerConversations: RunnerConversation[] = await fetchRunnerConversations(
    recentlyRun.filter((schedule) => !directlyLinked.has(schedule.id))
  );

  return {
    agentSchedules,
    modelSchedules,
    outputParams,
    assetGroups,
    agentRuns,
    runnerConversations,
    failedSources,
  };
};

// Kept off the main query: it downloads from S3 and hits the datasource REST API, and would hold the masthead behind both
/** The composed forecasts that can answer "actual vs forecast" — one list query, with no S3 download behind it. */
const fetchGenerationPairs = async (): Promise<GenerationPair[]> => {
  // ComposedForecast grants the Admin group a global read too — same scoping as the main fan-out
  const items = await fetchAllPages<ComposedForecastLite>(
    listComposedForecastsCustom,
    "listComposedForecasts",
    { filter: await getAdminOwnerFilter(), limit: COMPOSED_FORECAST_PAGE_SIZE },
    "composed forecasts"
  );
  return selectGenerationPairs(items);
};

/** One series with the source's weight applied (the Custom View's multiplier), so the tile compares like with like. */
const fetchSeries = async (source: GenerationSeriesSource, label: string, weight?: number) =>
  weightPreviewData(await fetchSourceSeries(source, label), weight);

/** One series, whichever of the fetchable kinds it is — the only place a source shape becomes a fetch. */
const fetchSourceSeries = (source: GenerationSeriesSource, label: string) => {
  if (source.kind === "model") {
    return fetchPreviewData("PROCESSED_MODEL_OUTPUT", {
      sourceType: "PROCESSED_MODEL_OUTPUT",
      sourceName: label,
      processedModelOutputId: source.outputParamId,
      weight: 1,
      priority: 0,
    });
  }

  if (source.kind === "postgres") {
    return fetchPreviewData("POSTGRES_HISTORICAL", {
      sourceType: "POSTGRES_HISTORICAL",
      sourceName: label,
      parameterId: source.parameterId,
      lookbackDays: source.lookbackDays,
      weight: 1,
      priority: 0,
    });
  }

  return fetchPreviewData("DATASET", {
    sourceType: "DATASET",
    sourceName: label,
    datasetId: source.datasetId,
    datasetRecordS3Path: source.datasetRecordS3Path,
    datasetRecordFileName: source.datasetRecordFileName,
    datasetName: source.datasetName,
    operation: source.operation,
    curated: source.curated,
    column: source.column,
    lookbackDays: source.lookbackDays,
    weight: 1,
    priority: 0,
  });
};

/** Every non-forecast source. One failing must not take the chart down, so each resolves to null instead of throwing. */
const fetchCandidateSeries = (pair: GenerationPair): Promise<CandidateSeries[]> =>
  Promise.all(
    pair.candidates.map(async (candidate) => {
      try {
        return {
          id: candidate.id,
          label: candidate.label,
          series: await fetchSeries(candidate.source, candidate.label, candidate.weight),
        };
      } catch (error) {
        console.error(`[useOperationsData] Source ${candidate.label} could not be fetched:`, error);
        return { id: candidate.id, label: candidate.label, series: null };
      }
    })
  );

/** One pair's comparison, always its own query and cached per pair, so an added tile never slows those already on screen. */
const fetchComparisonForPair = async (
  pair: GenerationPair
): Promise<GenerationComparison | null> => {
  const [forecastSeries, candidateSeries] = await Promise.all([
    fetchSeries({ kind: "model", outputParamId: pair.outputParamId }, pair.forecastSourceName, pair.forecastWeight),
    fetchCandidateSeries(pair),
  ]);

  return compareGenerationToForecast(pair, forecastSeries, candidateSeries);
};

/** The add-widget catalogue: every composed forecast that pairs a model against an actual feed. */
export const useGenerationPairs = () => {
  const { data, isLoading } = useQuery({
    queryKey: [...OPERATIONS_QUERY_KEY, "generationPairs"],
    queryFn: fetchGenerationPairs,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  return { pairs: data ?? [], isLoading };
};

/** Generation vs forecast, on its own query so its latency never blocks the board; with no pair it takes the freshest. */
const useGenerationComparison = (pair?: GenerationPair | null) => {
  // Off the shared pairs query: re-listing every composed forecast to find the freshest ran the paginated query twice per load
  const { pairs, isLoading: isPairsLoading } = useGenerationPairs();
  // The list already arrives freshest-first, so the default pick is simply the head of it
  const target = pair ?? pairs[0] ?? null;

  const { data, isLoading, error } = useQuery({
    // The per-pair key the tiles and the map use, so a pair that is charted AND tiled is fetched once
    queryKey: [...OPERATIONS_QUERY_KEY, "comparison", target?.composedForecastId ?? "none"],
    queryFn: () => fetchComparisonForPair(target!),
    enabled: Boolean(target),
    staleTime: 5 * 60_000,
    // One S3 file plus one REST page is not worth retrying twice on a dashboard the user can refresh
    retry: 1,
  });

  return {
    comparison: data ?? null,
    // A disabled query reports isLoading forever, and the pairs must settle first, or the empty state flashes on every load
    isLoading: isPairsLoading || (Boolean(target) && isLoading),
    error,
  };
};

/** One KPI tile per added forecast, each its own query, so a fourth cannot slow the three already there. */
export const useComposedForecastKpis = (pairs: GenerationPair[]): Kpi[] => {
  const results = useQueries({
    queries: pairs.map((pair) => ({
      queryKey: [...OPERATIONS_QUERY_KEY, "comparison", pair.composedForecastId],
      queryFn: () => fetchComparisonForPair(pair),
      staleTime: 5 * 60_000,
      retry: 1,
    })),
  });

  return useMemo(
    () =>
      pairs.map((pair, index) => {
        const result = results[index];
        return generationKpi(
          result?.data ?? null,
          null,
          Date.now(),
          result?.isLoading ?? false,
          Boolean(result?.error),
          pair.composedForecastName,
          composedForecastTileId(pair.composedForecastId)
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- settled values, since the result objects are new every render
    [pairs, results.map((r) => `${r.isLoading}:${Boolean(r.error)}:${r.dataUpdatedAt}`).join("|")]
  );
};

/** Live readings for the map, on the tiles' own query key, so a forecast already charted or tiled is not fetched twice. */
export const useAssetComparisons = (
  cards: AssetCard[],
  pairs: GenerationPair[]
): Map<string, GenerationComparison | null> => {
  const wanted = useMemo(() => assetForecastPairs(cards, pairs), [cards, pairs]);

  const results = useQueries({
    queries: wanted.map((pair) => ({
      queryKey: [...OPERATIONS_QUERY_KEY, "comparison", pair.composedForecastId],
      queryFn: () => fetchComparisonForPair(pair),
      staleTime: 5 * 60_000,
      retry: 1,
    })),
  });

  return useMemo(
    () => new Map(wanted.map((pair, index) => [pair.composedForecastId, results[index]?.data ?? null])),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- settled values, since the result objects are new every render
    [wanted, results.map((r) => r.dataUpdatedAt).join("|")]
  );
};

export const useOperationsData = (
  timeWindow: TimeWindow = DEFAULT_TIME_WINDOW,
  pair?: GenerationPair | null
) => {
  const queryClient = useQueryClient();
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: OPERATIONS_QUERY_KEY,
    queryFn: fetchOperationsData,
    staleTime: 60_000,
  });
  const runHours = runFetchHours(timeWindow);
  const runHistory = useQuery({
    queryKey: [...OPERATIONS_QUERY_KEY, "runs", runHours],
    queryFn: () => fetchRunHistory(runHours),
    staleTime: 60_000,
    // Keeps the board up on the old range while a longer one loads, instead of dropping to skeletons
    keepPreviousData: true,
  });
  const {
    comparison,
    isLoading: isComparisonLoading,
    error: comparisonError,
  } = useGenerationComparison(pair);

  // Prefix match, so Refresh reloads the chart, the pair list and every added tile, not only the fast query
  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: OPERATIONS_QUERY_KEY }),
    [queryClient]
  );
  const isRefreshing = useIsFetching({ queryKey: OPERATIONS_QUERY_KEY }) > 0;

  // Writes one saved agent into the cache, so a pause or run re-derives the board without refetching every source
  const patchAgentSchedule = useCallback(
    (updated: AgentSchedule) =>
      queryClient.setQueryData<BoardData>(OPERATIONS_QUERY_KEY, (old) =>
        old ? { ...old, agentSchedules: mergeAgentSchedule(old.agentSchedules, updated) } : old
      ),
    [queryClient]
  );

  // Derivation is cheap but runs over every row, so it is keyed to the fetch rather than to each render
  const derived = useMemo(() => {
    const empty: BoardData = {
      agentSchedules: [],
      agentRuns: [],
      modelSchedules: [],
      outputParams: [],
      assetGroups: [],
      runnerConversations: [],
      failedSources: [],
    };
    const fetched = data || empty;
    // Widening the range shows the narrower runs until the longer fetch lands, so the counts are flagged short meanwhile
    const runsCoverLess = Boolean(runHistory.data && runHistory.data.hours < runHours);
    // A rejected fetch keeps the last good data, so it is named alongside any source that failed inside the fetch
    const source: OperationsData = {
      ...fetched,
      runs: runHistory.data?.runs ?? [],
      modelSchemas: runHistory.data?.modelSchemas ?? [],
      failedSources: [
        ...fetched.failedSources,
        ...(runHistory.data?.failedSources ?? []),
        ...(runHistory.error ? ["run history"] : []),
        ...(runsCoverLess ? ["run history for this range (still loading)"] : []),
        ...(error ? ["dashboard data"] : []),
      ],
    };
    const signals = deriveSignals(source);
    const attention = selectNeedsAttention(signals, Date.now(), timeWindow);
    // Grouped before display so a subject that failed four times is one row, not four
    const needsAttention = groupSignals(attention);
    return {
      signals,
      needsAttention,
      agents: summarizeAgents(source.agentSchedules),
      // Ungrouped, since assetCards picks the worst signal per schema itself; runs give each card its activity count
      assets: assetCards(source.assetGroups, attention, source.runs, Date.now(), timeWindow),
      // Reads the answers the agents already wrote — no new query, and no new tool in erode-mcp
      insights: latestAnalystInsights(
        source.agentRuns,
        source.agentSchedules,
        source.runnerConversations
      ),
      kpis: operationsKpis(source, signals, needsAttention, Date.now(), timeWindow),
      // The generation number is no longer a row tile — the chart widget renders it, so it travels on its own
      generationHeadline: generationHeadline(
        source,
        comparison,
        Date.now(),
        isComparisonLoading,
        Boolean(comparisonError)
      ),
      failedSources: source.failedSources,
    };
  }, [
    data,
    error,
    runHistory.data,
    runHistory.error,
    runHours,
    comparison,
    isComparisonLoading,
    comparisonError,
    timeWindow,
  ]);

  // The older of the two, so the board never looks fresher than its run history
  const updatedAtMs = [dataUpdatedAt, runHistory.dataUpdatedAt].filter(Boolean);

  return {
    ...derived,
    comparison,
    isComparisonLoading,
    comparisonError,
    isLoading: isLoading || runHistory.isLoading,
    isRefreshing,
    error,
    refresh,
    patchAgentSchedule,
    // Every widget frame requires this — a stale dashboard that looks live is the worst failure here
    updatedAt: updatedAtMs.length ? new Date(Math.min(...updatedAtMs)).toISOString() : null,
  };
};

export default useOperationsData;
